import { z } from "zod";
// The staff diagnostics contract (CHT-772, Phase 13.6). Routes in `apps/api/src/routes/staff-diagnostics-routes.ts`; the admin calls them only
// through `@lafayette/api-client` (`staff-diagnostics.ts`).
//
//   GET  /staff/audit/events                      the audit view, filtered, keyset pages          `audit.read` (supervisor, administrator)
//   GET  /staff/operations/notification-failures  deliveries that ended FAILED, keyset pages      `operations.jobs.read`
//   GET  /staff/payment-exceptions                the open payment reconciliation exceptions      `payment.resolve_exception` (supervisor)
//   POST /staff/payment-exceptions/:id/resolve    resolves one exception, `Idempotency-Key`       `payment.resolve_exception`
//
// STAFF ONLY and closed, like `staff-team.ts`: strict shapes (an unlisted field fails the parse), ids, closed codes, staff reasons and times.
// No message text, no memory value, no note, no `before` or `after` metadata, no idempotency key, no push token, no provider text.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const instant = z.iso.datetime({ offset: false });
/** An id of any record: a UUID, or the short id a log may use. Never free text. */
const recordId = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u);
/** A stored id the audit log holds (an actor, a target, a correlation): bounded text, never a message. */
const storedId = z.string().min(1).max(128);
/** A closed code: upper case letters, digits and `_`. */
const closedCode = z.string().regex(/^[A-Z][A-Z0-9_]{0,63}$/u);
const staffReason = z.string().trim().min(1).max(500);
const pageCursor = z
    .string()
    .min(1)
    .max(300)
    .regex(/^[A-Za-z0-9_-]+$/u);
export const STAFF_AUDIT_EVENTS_PATH = "/staff/audit/events";
export const STAFF_NOTIFICATION_FAILURES_PATH = "/staff/operations/notification-failures";
export const STAFF_PAYMENT_EXCEPTIONS_PATH = "/staff/payment-exceptions";
export const STAFF_PAYMENT_EXCEPTION_RESOLVE_PATH = "/staff/payment-exceptions/:exceptionId/resolve";
export const STAFF_DIAGNOSTICS_PAGE_MAX = 100;
// --- the audit view ----------------------------------------------------------------------------------------------------------
/** An audit action (`payment_request.cancelled`) or the family before its first dot (`payment_request`). */
const auditAction = z
    .string()
    .max(100)
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/u);
export const staffAuditQuerySchema = z.strictObject({
    actorId: recordId.optional(),
    action: auditAction.optional(),
    targetType: z
        .string()
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/u)
        .optional(),
    targetId: recordId.optional(),
    from: z.iso
        .datetime({ offset: true })
        .transform((value) => new Date(value))
        .optional(),
    to: z.iso
        .datetime({ offset: true })
        .transform((value) => new Date(value))
        .optional(),
    cursor: pageCursor.optional(),
    limit: z.coerce.number().int().min(1).max(STAFF_DIAGNOSTICS_PAGE_MAX).optional(),
});
export const staffAuditActorKindValues = ["customer", "staff", "system"];
export const staffAuditEventSchema = z.strictObject({
    id: uuid,
    /** The stable order of the log. */
    sequence: z.number().int().min(1),
    occurredAt: instant,
    actor: z.strictObject({
        kind: z.enum(staffAuditActorKindValues),
        id: storedId,
        /** The display name of a staff actor that still exists, else `null`. */
        displayName: z.string().min(1).max(200).nullable(),
    }),
    action: auditAction,
    target: z.strictObject({
        type: z.string().regex(/^[a-z][a-z0-9_]*$/u),
        id: storedId,
    }),
    /** The staff reason. `null` for a customer actor and for an action with no reason. */
    reason: z.string().max(1000).nullable(),
    /** The closed reason code of a job re-run or pause. `null` when the action has none. Never raw metadata. */
    reasonCode: closedCode.nullable(),
    /** The closed resolution code of a payment exception. `null` when the action has none. Never raw metadata. */
    resolutionCode: closedCode.nullable(),
    /** CHT-2027: the tab of an analytics opening or denial. `null` for every other action. */
    tab: z.enum(["overview", "explore", "events"]).nullable(),
    correlationId: storedId,
});
/** `GET /staff/audit/events` */
export const staffAuditPageSchema = z.strictObject({
    items: z.array(staffAuditEventSchema).max(STAFF_DIAGNOSTICS_PAGE_MAX),
    nextCursor: pageCursor.nullable(),
});
// --- notification failures -----------------------------------------------------------------------------------------------------
export const staffNotificationFailuresQuerySchema = z.strictObject({
    cursor: pageCursor.optional(),
    limit: z.coerce.number().int().min(1).max(STAFF_DIAGNOSTICS_PAGE_MAX).optional(),
});
/**
 * The CLOSED receipt failure codes of the operator view (CHT-764 review fix 8). The domain list is `pushReceiptFailureCodes`; a parity test
 * in `apps/api` fails when the two differ.
 */
export const staffReceiptFailureCodeValues = [
    "DEVICE_NOT_REGISTERED",
    "MESSAGE_TOO_BIG",
    "MESSAGE_RATE_EXCEEDED",
    "INVALID_CREDENTIALS",
    "MISMATCH_SENDER_ID",
    "MISSING",
    "OTHER",
];
export const staffNotificationFailureSchema = z.strictObject({
    deliveryId: uuid,
    notificationEventId: uuid,
    kind: z.string().regex(/^[A-Z][A-Z0-9_]{0,63}$/u),
    audience: z.enum(["CUSTOMER", "STAFF"]),
    /** `DELIVERY`: the attempt ended FAILED. `RECEIPT`: Expo accepted it, and a receipt came back with an error or never came. */
    source: z.enum(["DELIVERY", "RECEIPT"]),
    /** A closed upper case code. Never provider text. */
    reasonCode: z.string().regex(/^[A-Z][A-Z0-9_]{0,63}$/u),
    /** The closed receipt failure codes of a `RECEIPT` row (each once), empty for a `DELIVERY` row. No token and no ticket id. */
    receiptCodes: z
        .array(z.enum(staffReceiptFailureCodeValues))
        .max(staffReceiptFailureCodeValues.length),
    attempts: z.number().int().min(0).max(100),
    occurredAt: instant,
    subject: z.strictObject({
        type: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
        id: uuid,
    }),
});
/** `GET /staff/operations/notification-failures` */
export const staffNotificationFailurePageSchema = z.strictObject({
    generatedAt: instant,
    items: z.array(staffNotificationFailureSchema).max(STAFF_DIAGNOSTICS_PAGE_MAX),
    nextCursor: pageCursor.nullable(),
});
// --- payment exceptions ----------------------------------------------------------------------------------------------------
export const staffPaymentExceptionKindValues = [
    "DUPLICATE_CONFLICT",
    "UNKNOWN_ORDER",
    "ORDER_MISMATCH",
    "CUSTOMER_MISMATCH",
    "AMOUNT_MISMATCH",
    "CURRENCY_MISMATCH",
    "LATE_SETTLEMENT",
    "REQUEST_WITHDRAWN",
    "UNEXPECTED_STATE",
    "UNKNOWN_REFUND",
    "REFUND_MISMATCH",
];
/** The closed resolution codes of the domain (`reconciliationResolutionCodes`; a parity test in `apps/api` fails when one differs). */
export const staffPaymentResolutionCodeValues = [
    "REFUNDED_OUT_OF_BAND",
    "CONFIRMED_NO_MONEY_MOVED",
    "ADOPTED_REFUND",
    "DUPLICATE_IGNORED",
    "OTHER",
];
export const staffPaymentExceptionSchema = z.strictObject({
    id: uuid,
    kind: z.enum(staffPaymentExceptionKindValues),
    provider: z.string().regex(/^[a-z][a-z0-9_]{0,31}$/u),
    paymentRequestId: uuid.nullable(),
    refundId: uuid.nullable(),
    openedAt: instant,
    /** The version a resolve command sends back. */
    version: z.number().int().min(1),
});
/** `GET /staff/payment-exceptions` */
export const staffPaymentExceptionListSchema = z.strictObject({
    items: z.array(staffPaymentExceptionSchema).max(STAFF_DIAGNOSTICS_PAGE_MAX),
});
/** `POST /staff/payment-exceptions/:exceptionId/resolve`. The version is the one the supervisor read; a stale one is a 409. */
export const staffResolvePaymentExceptionBodySchema = z.strictObject({
    expectedVersion: z.number().int().min(1),
    resolutionCode: z.enum(staffPaymentResolutionCodeValues),
    reason: staffReason,
});
/** The outcome of a resolve command: flat ids and codes. */
export const staffResolvePaymentExceptionOutcomeSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]));
